<template>
  <!-- Product variant: configurable avatar + lines -->
  <div
    v-if="variant === 'product'"
    class="skeleton-product"
    :data-lines-xl="linesCountXl"
    :data-lines-2xl="linesCount2xl"
    :style="productContainerStyle"
    aria-label="Loading"
    role="status"
    aria-live="polite"
  >
    <div
      v-if="showAvatar"
      class="skeleton skeleton-avatar"
      :class="avatarRoundedClass"
      :style="productAvatarStyle"
    />
    <div class="skeleton-product-lines">
      <div
        v-for="(line, index) in visibleLines"
        :key="index"
        class="skeleton skeleton-line-product rounded-md"
        :class="`skeleton-line-product-${index + 1}`"
        :style="getLineStyle(index)"
      />
    </div>
  </div>
  
  <!-- Avatar-line variant: circle + horizontal bar -->
  <div
    v-else-if="variant === 'avatar-line'"
    class="skeleton-avatar-line"
    :style="avatarLineContainerStyle"
    aria-label="Loading"
    role="status"
    aria-live="polite"
  >
    <div
      class="skeleton skeleton-circle"
      :style="circleStyle"
    />
    <div
      class="skeleton skeleton-line rounded-md"
      :style="lineStyle"
    />
  </div>
  
  <!-- Default variant: single skeleton bar -->
  <div
    v-else
    class="skeleton"
    :class="roundedClass"
    :style="skeletonStyle"
    aria-label="Loading"
    role="status"
    aria-live="polite"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  width?: string | number
  height?: string | number
  rounded?: boolean
  maxWidth?: string | number
  variant?: 'default' | 'avatar-line' | 'product'
  lineWidth?: string | number // For avatar-line variant: width of the horizontal bar
  // Responsive width props
  widthSm?: string | number
  widthMd?: string | number
  widthLg?: string | number
  widthXl?: string | number
  width2xl?: string | number
  // Responsive height props
  heightSm?: string | number
  heightMd?: string | number
  heightLg?: string | number
  heightXl?: string | number
  height2xl?: string | number
  // Responsive lineWidth props (for avatar-line variant)
  lineWidthSm?: string | number
  lineWidthMd?: string | number
  lineWidthLg?: string | number
  lineWidthXl?: string | number
  lineWidth2xl?: string | number
  // Product variant props
  showAvatar?: boolean
  avatarRounded?: 'lg' | 'full' | 'none' // 'lg' = rounded-lg, 'full' = rounded-full, 'none' = rounded-md
  avatarSize?: string | number // Size of avatar (width and height)
  linesCount?: number // Number of lines to show (default: 2 for 2xl, 1 for xl)
  linesCountXl?: number // Number of lines on XL screens (default: 1)
  linesCount2xl?: number // Number of lines on 2XL screens (default: 2)
  // Line sizes - can be array or individual props
  lineWidths?: (string | number)[] // Array of widths for each line
  lineHeights?: (string | number)[] // Array of heights for each line
  // Legacy props for backwards compatibility
  productLine1Width?: string | number
  productLine1Height?: string | number
  productLine2Width?: string | number
  productLine2Height?: string | number
  // Responsive line size props
  productLine1WidthXl?: string | number
  productLine1HeightXl?: string | number
  productLine1Width2xl?: string | number
  productLine1Height2xl?: string | number
  productLine2WidthXl?: string | number
  productLine2HeightXl?: string | number
  productLine2Width2xl?: string | number
  productLine2Height2xl?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  width: '100%',
  height: '1rem',
  rounded: false,
  maxWidth: '100%',
  variant: 'default',
  lineWidth: '100%',
  showAvatar: true,
  avatarRounded: 'lg',
  avatarSize: 40,
  linesCount: 2,
  linesCountXl: 1,
  linesCount2xl: 2
})

const roundedClass = computed(() => {
  return props.rounded ? 'rounded-full' : 'rounded-md'
})

// Helper to convert value to CSS unit
const toCssValue = (value: string | number | undefined, defaultVal: string): string => {
  if (value === undefined) return defaultVal
  if (typeof value === 'number') return `${value}px`
  // If string is a pure number, add 'px'
  if (/^\d+$/.test(value)) return `${value}px`
  // Otherwise use as-is (already has unit like '100%', '1rem', etc.)
  return value
}

// Helper to convert value to percentage (for product line widths)
const toPercentValue = (value: string | number | undefined, defaultVal: string): string => {
  if (value === undefined) return defaultVal
  if (typeof value === 'number') return `${value}%`
  // If string is a pure number, add '%'
  if (/^\d+$/.test(value)) return `${value}%`
  // Otherwise use as-is (already has unit like '100%', '152%', etc.)
  return value
}

// Default skeleton style (for single bar variant)
const skeletonStyle = computed(() => {
  const style: Record<string, string> = {}
  
  // Base width/height (used as CSS custom property defaults)
  style['--skeleton-width-base'] = toCssValue(props.width, '100%')
  style['--skeleton-height-base'] = toCssValue(props.height, '1rem')
  
  // Responsive width custom properties
  if (props.widthSm !== undefined) {
    style['--skeleton-width-sm'] = toCssValue(props.widthSm, '')
  }
  if (props.widthMd !== undefined) {
    style['--skeleton-width-md'] = toCssValue(props.widthMd, '')
  }
  if (props.widthLg !== undefined) {
    style['--skeleton-width-lg'] = toCssValue(props.widthLg, '')
  }
  if (props.widthXl !== undefined) {
    style['--skeleton-width-xl'] = toCssValue(props.widthXl, '')
  }
  if (props.width2xl !== undefined) {
    style['--skeleton-width-2xl'] = toCssValue(props.width2xl, '')
  }
  
  // Responsive height custom properties
  if (props.heightSm !== undefined) {
    style['--skeleton-height-sm'] = toCssValue(props.heightSm, '')
  }
  if (props.heightMd !== undefined) {
    style['--skeleton-height-md'] = toCssValue(props.heightMd, '')
  }
  if (props.heightLg !== undefined) {
    style['--skeleton-height-lg'] = toCssValue(props.heightLg, '')
  }
  if (props.heightXl !== undefined) {
    style['--skeleton-height-xl'] = toCssValue(props.heightXl, '')
  }
  if (props.height2xl !== undefined) {
    style['--skeleton-height-2xl'] = toCssValue(props.height2xl, '')
  }
  
  // Handle maxWidth (non-responsive for now)
  if (props.maxWidth !== undefined) {
    style.maxWidth = toCssValue(props.maxWidth, '100%')
  }
  
  // Set initial width/height from base values (for backwards compatibility)
  // Only set inline styles if no responsive props are provided (to allow CSS custom properties to work)
  const hasResponsiveHeight = props.heightSm !== undefined || props.heightMd !== undefined || 
                              props.heightLg !== undefined || props.heightXl !== undefined || 
                              props.height2xl !== undefined
  const hasResponsiveWidth = props.widthSm !== undefined || props.widthMd !== undefined || 
                             props.widthLg !== undefined || props.widthXl !== undefined || 
                             props.width2xl !== undefined
  
  if (!hasResponsiveWidth) {
    style.width = toCssValue(props.width, '100%')
  }
  if (!hasResponsiveHeight) {
    style.height = toCssValue(props.height, '1rem')
  }
  
  return style
})

// Avatar-line container style (to pass custom properties to children)
const avatarLineContainerStyle = computed(() => {
  const style: Record<string, string> = {}
  
  // Pass all height custom properties to container so children can access them
  style['--skeleton-line-height-base'] = toCssValue(props.height, '20px')
  if (props.heightSm !== undefined) {
    style['--skeleton-line-height-sm'] = toCssValue(props.heightSm, '')
  }
  if (props.heightMd !== undefined) {
    style['--skeleton-line-height-md'] = toCssValue(props.heightMd, '')
  }
  if (props.heightLg !== undefined) {
    style['--skeleton-line-height-lg'] = toCssValue(props.heightLg, '')
  }
  if (props.heightXl !== undefined) {
    style['--skeleton-line-height-xl'] = toCssValue(props.heightXl, '')
  }
  if (props.height2xl !== undefined) {
    style['--skeleton-line-height-2xl'] = toCssValue(props.height2xl, '')
  }
  
  // Also pass lineWidth custom properties
  style['--skeleton-line-width-base'] = toCssValue(props.lineWidth, '100%')
  if (props.lineWidthSm !== undefined) {
    style['--skeleton-line-width-sm'] = toCssValue(props.lineWidthSm, '')
  }
  if (props.lineWidthMd !== undefined) {
    style['--skeleton-line-width-md'] = toCssValue(props.lineWidthMd, '')
  }
  if (props.lineWidthLg !== undefined) {
    style['--skeleton-line-width-lg'] = toCssValue(props.lineWidthLg, '')
  }
  if (props.lineWidthXl !== undefined) {
    style['--skeleton-line-width-xl'] = toCssValue(props.lineWidthXl, '')
  }
  if (props.lineWidth2xl !== undefined) {
    style['--skeleton-line-width-2xl'] = toCssValue(props.lineWidth2xl, '')
  }
  
  return style
})

// Circle style for avatar-line variant
const circleStyle = computed(() => {
  return {
    width: '40px',
    height: '40px'
  }
})

// Avatar rounded class for product variant
const avatarRoundedClass = computed(() => {
  if (props.avatarRounded === 'full') return 'rounded-full'
  if (props.avatarRounded === 'lg') return 'rounded-lg'
  return 'rounded-md'
})

// Product variant styles
const productAvatarStyle = computed(() => {
  const size = toCssValue(props.avatarSize, '40px')
  return {
    width: size,
    height: size
  }
})

// Product container style with CSS custom properties for responsive line sizes
const productContainerStyle = computed(() => {
  const style: Record<string, string> = {}
  
  // Line 1 base values
  if (props.productLine1Width !== undefined) {
    style['--product-line-1-width-base'] = toPercentValue(props.productLine1Width, '152%')
  }
  if (props.productLine1Height !== undefined) {
    style['--product-line-1-height-base'] = toCssValue(props.productLine1Height, '20px')
  }
  
  // Line 1 XL values
  if (props.productLine1WidthXl !== undefined) {
    style['--product-line-1-width-xl'] = toPercentValue(props.productLine1WidthXl, '')
  }
  if (props.productLine1HeightXl !== undefined) {
    style['--product-line-1-height-xl'] = toCssValue(props.productLine1HeightXl, '')
  }
  
  // Line 1 2XL values
  if (props.productLine1Width2xl !== undefined) {
    style['--product-line-1-width-2xl'] = toPercentValue(props.productLine1Width2xl, '')
  }
  if (props.productLine1Height2xl !== undefined) {
    style['--product-line-1-height-2xl'] = toCssValue(props.productLine1Height2xl, '')
  }
  
  // Line 2 base values
  if (props.productLine2Width !== undefined) {
    style['--product-line-2-width-base'] = toPercentValue(props.productLine2Width, '27%')
  }
  if (props.productLine2Height !== undefined) {
    style['--product-line-2-height-base'] = toCssValue(props.productLine2Height, '20px')
  }
  
  // Line 2 XL values
  if (props.productLine2WidthXl !== undefined) {
    style['--product-line-2-width-xl'] = toPercentValue(props.productLine2WidthXl, '')
  }
  if (props.productLine2HeightXl !== undefined) {
    style['--product-line-2-height-xl'] = toCssValue(props.productLine2HeightXl, '')
  }
  
  // Line 2 2XL values
  if (props.productLine2Width2xl !== undefined) {
    style['--product-line-2-width-2xl'] = toPercentValue(props.productLine2Width2xl, '')
  }
  if (props.productLine2Height2xl !== undefined) {
    style['--product-line-2-height-2xl'] = toCssValue(props.productLine2Height2xl, '')
  }
  
  return style
})

// Get line style for a specific line index
// Note: Width and height are handled by CSS custom properties, not inline styles
// This allows responsive breakpoints to override the values
const getLineStyle = (index: number) => {
  const style: Record<string, string> = {
    flexShrink: '0'
  }
  
  // Only set width/height if using lineWidths/lineHeights arrays (for non-product variants)
  // For product variant, CSS handles width/height via custom properties
  if (props.variant === 'product') {
    // Product variant uses CSS custom properties set on container
    // No inline width/height needed - CSS will handle it
    return style
  }
  
  // For non-product variants, use inline styles
  if (props.lineWidths && props.lineWidths[index] !== undefined) {
    style.width = toPercentValue(props.lineWidths[index], '100%')
  } else {
    style.width = '100%'
  }
  
  if (props.lineHeights && props.lineHeights[index] !== undefined) {
    style.height = toCssValue(props.lineHeights[index], '20px')
  } else {
    style.height = '20px'
  }
  
  return style
}

// Compute visible lines count based on screen size (will be controlled by CSS)
const maxLines = computed(() => {
  return Math.max(
    props.linesCount || 2,
    props.linesCount2xl || 2,
    props.linesCountXl || 1
  )
})

// Generate array of line indices for v-for
const visibleLines = computed(() => {
  return Array.from({ length: maxLines.value }, (_, i) => i)
})

// Computed props for data attributes
const linesCountXl = computed(() => props.linesCountXl ?? 1)
const linesCount2xl = computed(() => props.linesCount2xl ?? 2)

// Line style for avatar-line variant
const lineStyle = computed(() => {
  const style: Record<string, string> = {}
  
  // Base lineWidth/height (used as CSS custom property defaults)
  style['--skeleton-line-width-base'] = toCssValue(props.lineWidth, '100%')
  style['--skeleton-line-height-base'] = toCssValue(props.height, '20px')
  
  // Responsive lineWidth custom properties
  if (props.lineWidthSm !== undefined) {
    style['--skeleton-line-width-sm'] = toCssValue(props.lineWidthSm, '')
  }
  if (props.lineWidthMd !== undefined) {
    style['--skeleton-line-width-md'] = toCssValue(props.lineWidthMd, '')
  }
  if (props.lineWidthLg !== undefined) {
    style['--skeleton-line-width-lg'] = toCssValue(props.lineWidthLg, '')
  }
  if (props.lineWidthXl !== undefined) {
    style['--skeleton-line-width-xl'] = toCssValue(props.lineWidthXl, '')
  }
  if (props.lineWidth2xl !== undefined) {
    style['--skeleton-line-width-2xl'] = toCssValue(props.lineWidth2xl, '')
  }
  
  // Responsive height custom properties for line
  if (props.heightSm !== undefined) {
    style['--skeleton-line-height-sm'] = toCssValue(props.heightSm, '')
  }
  if (props.heightMd !== undefined) {
    style['--skeleton-line-height-md'] = toCssValue(props.heightMd, '')
  }
  if (props.heightLg !== undefined) {
    style['--skeleton-line-height-lg'] = toCssValue(props.heightLg, '')
  }
  if (props.heightXl !== undefined) {
    style['--skeleton-line-height-xl'] = toCssValue(props.heightXl, '')
  }
  if (props.height2xl !== undefined) {
    style['--skeleton-line-height-2xl'] = toCssValue(props.height2xl, '')
  }
  
  // Set initial width/height from base values (for backwards compatibility)
  // Only set inline styles if no responsive props are provided (to allow CSS custom properties to work)
  const hasResponsiveHeight = props.heightSm !== undefined || props.heightMd !== undefined || 
                              props.heightLg !== undefined || props.heightXl !== undefined || 
                              props.height2xl !== undefined
  const hasResponsiveWidth = props.lineWidthSm !== undefined || props.lineWidthMd !== undefined || 
                             props.lineWidthLg !== undefined || props.lineWidthXl !== undefined || 
                             props.lineWidth2xl !== undefined
  
  if (!hasResponsiveWidth) {
    style.width = toCssValue(props.lineWidth, '100%')
  }
  if (!hasResponsiveHeight) {
    style.height = toCssValue(props.height, '20px')
  }
  
  return style
})
</script>

<style scoped>
/* Base skeleton styles */
.skeleton {
  position: relative;
  display: block;
  overflow: hidden;
  background-color: var(--skeleton-bg, var(--color-gray-200));
  /* Base values from CSS custom properties, fallback to defaults */
  width: var(--skeleton-width-base, 100%);
  height: var(--skeleton-height-base, 1rem);
}

/* Responsive breakpoints - Tailwind default breakpoints */
/* sm: 640px */
@media (min-width: 640px) {
  .skeleton {
    width: var(--skeleton-width-sm, var(--skeleton-width-base, 100%));
    height: var(--skeleton-height-sm, var(--skeleton-height-base, 1rem));
  }
}

/* md: 768px */
@media (min-width: 768px) {
  .skeleton {
    width: var(--skeleton-width-md, var(--skeleton-width-sm, var(--skeleton-width-base, 100%)));
    height: var(--skeleton-height-md, var(--skeleton-height-sm, var(--skeleton-height-base, 1rem)));
  }
}

/* lg: 1024px */
@media (min-width: 1024px) {
  .skeleton {
    width: var(--skeleton-width-lg, var(--skeleton-width-md, var(--skeleton-width-sm, var(--skeleton-width-base, 100%))));
    height: var(--skeleton-height-lg, var(--skeleton-height-md, var(--skeleton-height-sm, var(--skeleton-height-base, 1rem))));
  }
}

/* xl: 1280px */
@media (min-width: 1280px) {
  .skeleton {
    width: var(--skeleton-width-xl, var(--skeleton-width-lg, var(--skeleton-width-md, var(--skeleton-width-sm, var(--skeleton-width-base, 100%)))));
    height: var(--skeleton-height-xl, var(--skeleton-height-lg, var(--skeleton-height-md, var(--skeleton-height-sm, var(--skeleton-height-base, 1rem)))));
  }
}

/* 2xl: 1536px */
@media (min-width: 1536px) {
  .skeleton {
    width: var(--skeleton-width-2xl, var(--skeleton-width-xl, var(--skeleton-width-lg, var(--skeleton-width-md, var(--skeleton-width-sm, var(--skeleton-width-base, 100%))))));
    height: var(--skeleton-height-2xl, var(--skeleton-height-xl, var(--skeleton-height-lg, var(--skeleton-height-md, var(--skeleton-height-sm, var(--skeleton-height-base, 1rem))))));
  }
}

.skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: skeleton-shimmer 1.2s infinite;
}

/* Avatar-line variant container */
.skeleton-avatar-line {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: var(--skeleton-line-height-base, 20px);
}

/* Responsive min-height for avatar-line container */
@media (min-width: 640px) {
  .skeleton-avatar-line {
    min-height: var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px));
  }
}

@media (min-width: 768px) {
  .skeleton-avatar-line {
    min-height: var(--skeleton-line-height-md, var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px)));
  }
}

@media (min-width: 1024px) {
  .skeleton-avatar-line {
    min-height: var(--skeleton-line-height-lg, var(--skeleton-line-height-md, var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px))));
  }
}

@media (min-width: 1280px) {
  .skeleton-avatar-line {
    min-height: var(--skeleton-line-height-xl, var(--skeleton-line-height-lg, var(--skeleton-line-height-md, var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px)))));
  }
}

@media (min-width: 1536px) {
  .skeleton-avatar-line {
    min-height: var(--skeleton-line-height-2xl, var(--skeleton-line-height-xl, var(--skeleton-line-height-lg, var(--skeleton-line-height-md, var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px))))));
  }
}

/* Circle skeleton (avatar) */
.skeleton-circle {
  flex-shrink: 0;
  border-radius: 50%;
  width: 40px;
  height: 40px;
}

/* Line skeleton (text bar) */
.skeleton-line {
  flex: 1;
  min-width: 0;
  /* Base values from CSS custom properties */
  width: var(--skeleton-line-width-base, 100%);
  height: var(--skeleton-line-height-base, 20px);
}

/* Responsive breakpoints for skeleton line - Tailwind default breakpoints */
/* sm: 640px */
@media (min-width: 640px) {
  .skeleton-line {
    width: var(--skeleton-line-width-sm, var(--skeleton-line-width-base, 100%));
    height: var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px));
  }
}

/* md: 768px */
@media (min-width: 768px) {
  .skeleton-line {
    width: var(--skeleton-line-width-md, var(--skeleton-line-width-sm, var(--skeleton-line-width-base, 100%)));
    height: var(--skeleton-line-height-md, var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px)));
  }
}

/* lg: 1024px */
@media (min-width: 1024px) {
  .skeleton-line {
    width: var(--skeleton-line-width-lg, var(--skeleton-line-width-md, var(--skeleton-line-width-sm, var(--skeleton-line-width-base, 100%))));
    height: var(--skeleton-line-height-lg, var(--skeleton-line-height-md, var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px))));
  }
}

/* xl: 1280px */
@media (min-width: 1280px) {
  .skeleton-line {
    width: var(--skeleton-line-width-xl, var(--skeleton-line-width-lg, var(--skeleton-line-width-md, var(--skeleton-line-width-sm, var(--skeleton-line-width-base, 100%)))));
    height: var(--skeleton-line-height-xl, var(--skeleton-line-height-lg, var(--skeleton-line-height-md, var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px)))));
  }
}

/* 2xl: 1536px */
@media (min-width: 1536px) {
  .skeleton-line {
    width: var(--skeleton-line-width-2xl, var(--skeleton-line-width-xl, var(--skeleton-line-width-lg, var(--skeleton-line-width-md, var(--skeleton-line-width-sm, var(--skeleton-line-width-base, 100%))))));
    height: var(--skeleton-line-height-2xl, var(--skeleton-line-height-xl, var(--skeleton-line-height-lg, var(--skeleton-line-height-md, var(--skeleton-line-height-sm, var(--skeleton-line-height-base, 20px))))));
  }
}

/* Product variant container */
.skeleton-product {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.skeleton-product-lines {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

/* Product line skeleton styles */
.skeleton-line-product {
  flex-shrink: 0;
}

/* Base styles for product lines (use CSS custom properties) */
.skeleton-line-product-1 {
  width: var(--product-line-1-width-base, 100%);
  height: var(--product-line-1-height-base, 20px);
}

.skeleton-line-product-2 {
  width: var(--product-line-2-width-base, 100%);
  height: var(--product-line-2-height-base, 20px);
}

/* Default: Hide all lines except first (for screens below XL) */
.skeleton-line-product-2,
.skeleton-line-product-3,
.skeleton-line-product-4,
.skeleton-line-product-5 {
  display: none;
}

/* XL screens (1280px - 1535px): Show lines based on data-lines-xl */
@media (min-width: 1280px) and (max-width: 1535px) {
  .skeleton-product-lines {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  /* Always show first line */
  .skeleton-line-product-1 {
    display: block;
    /* Apply XL-specific sizes if provided */
    width: var(--product-line-1-width-xl, var(--product-line-1-width-base, 100%));
    height: var(--product-line-1-height-xl, var(--product-line-1-height-base, 20px));
  }
  
  .skeleton-line-product-2 {
    /* Apply XL-specific sizes if provided */
    width: var(--product-line-2-width-xl, var(--product-line-2-width-base, 100%));
    height: var(--product-line-2-height-xl, var(--product-line-2-height-base, 20px));
  }
  
  /* Show line 2 if data-lines-xl >= 2 */
  .skeleton-product[data-lines-xl="2"] .skeleton-line-product-2,
  .skeleton-product[data-lines-xl="3"] .skeleton-line-product-2,
  .skeleton-product[data-lines-xl="4"] .skeleton-line-product-2,
  .skeleton-product[data-lines-xl="5"] .skeleton-line-product-2 {
    display: block;
  }
  
  /* Show line 3 if data-lines-xl >= 3 */
  .skeleton-product[data-lines-xl="3"] .skeleton-line-product-3,
  .skeleton-product[data-lines-xl="4"] .skeleton-line-product-3,
  .skeleton-product[data-lines-xl="5"] .skeleton-line-product-3 {
    display: block;
  }
  
  /* Show line 4 if data-lines-xl >= 4 */
  .skeleton-product[data-lines-xl="4"] .skeleton-line-product-4,
  .skeleton-product[data-lines-xl="5"] .skeleton-line-product-4 {
    display: block;
  }
  
  /* Show line 5 if data-lines-xl >= 5 */
  .skeleton-product[data-lines-xl="5"] .skeleton-line-product-5 {
    display: block;
  }
  
  /* If more than 1 line, switch to row layout */
  .skeleton-product[data-lines-xl="2"] .skeleton-product-lines,
  .skeleton-product[data-lines-xl="3"] .skeleton-product-lines,
  .skeleton-product[data-lines-xl="4"] .skeleton-product-lines,
  .skeleton-product[data-lines-xl="5"] .skeleton-product-lines {
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }
}

/* 2XL screens (1536px+): Show lines based on data-lines-2xl */
@media (min-width: 1536px) {
  .skeleton-product-lines {
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }
  
  /* Always show first 2 lines */
  .skeleton-line-product-1,
  .skeleton-line-product-2 {
    display: block;
  }
  
  /* Apply 2XL-specific sizes if provided */
  .skeleton-line-product-1 {
    width: var(--product-line-1-width-2xl, var(--product-line-1-width-base, 100%));
    height: var(--product-line-1-height-2xl, var(--product-line-1-height-base, 20px));
  }
  
  .skeleton-line-product-2 {
    width: var(--product-line-2-width-2xl, var(--product-line-2-width-base, 100%));
    height: var(--product-line-2-height-2xl, var(--product-line-2-height-base, 20px));
  }
  
  /* Show line 3 if data-lines-2xl >= 3 */
  .skeleton-product[data-lines-2xl="3"] .skeleton-line-product-3,
  .skeleton-product[data-lines-2xl="4"] .skeleton-line-product-3,
  .skeleton-product[data-lines-2xl="5"] .skeleton-line-product-3 {
    display: block;
  }
  
  /* Show line 4 if data-lines-2xl >= 4 */
  .skeleton-product[data-lines-2xl="4"] .skeleton-line-product-4,
  .skeleton-product[data-lines-2xl="5"] .skeleton-line-product-4 {
    display: block;
  }
  
  /* Show line 5 if data-lines-2xl >= 5 */
  .skeleton-product[data-lines-2xl="5"] .skeleton-line-product-5 {
    display: block;
  }
}

/* Product avatar */
.skeleton-avatar {
  flex-shrink: 0;
}
</style>

